import * as React from "react"
import { useTranslation } from "react-i18next"

import { useUIPreferences } from "Catalog/shared/components/UIPreferencesContext"
import useStatefulField from "shared/hooks/useStatefulField"
import { EnergyUnit, IPer100Energy } from "shared/hubrise/models"
import { convertEnergy } from "shared/hubrise/utils/Nutrition"

import { RangeCheckbox, Container, EnergySelect, Selectors, StyledEnergyInput, Error } from "./Styles"

const numberToString = (value: number | null): string => (value !== null ? String(value) : "")

const stringToNumber = (value: string): number | null => (value === "" ? null : Number(value))

interface EnergySelectionProps {
  initialValue: IPer100Energy | null
  onChange: (energy: IPer100Energy | null) => void
}

const EnergySelection = ({ initialValue, onChange }: EnergySelectionProps): JSX.Element => {
  const { t } = useTranslation()
  const { defaultEnergyUnit, setDefaultEnergyUnit } = useUIPreferences()

  const [showRange, setShowRange] = React.useState(Boolean(initialValue?.upper))

  const {
    value: energy,
    handleChange,
    handleBlur,
    error,
  } = useStatefulField({
    initialValue,
    onChange,
    validate: (energy) => {
      if (energy === null) return
      if (energy.upper !== null && energy.upper <= energy.lower) {
        return t("nutrition.component.values.energy.validation.upper_must_be_higher")
      }
    },
  })

  const unit = energy?.unit ?? defaultEnergyUnit ?? "kcal"

  const patchEnergy = (patch: Partial<IPer100Energy>): IPer100Energy => {
    const current = energy ?? { unit, lower: 0, upper: null }
    return { ...current, ...patch }
  }

  const changeEnergyUnit = (newUnit: EnergyUnit) => {
    setDefaultEnergyUnit(newUnit)
    handleChange(energy ? convertEnergy(energy, newUnit) : patchEnergy({ unit: newUnit }))
  }

  return (
    <Container>
      <Selectors>
        <EnergySelect
          value={[unit]}
          searchable={false}
          options={["kcal", "kJ"].map((unit) => ({ value: unit, label: unit }))}
          onChange={(value) => changeEnergyUnit(value[0] as EnergyUnit)}
          onBlur={handleBlur}
        />

        <StyledEnergyInput
          value={numberToString(energy ? energy.lower : null)}
          placeholder={showRange ? t("nutrition.component.values.energy.lower") : undefined}
          filter={/^\d*\.?\d*$/}
          onChange={(val) => {
            const num = stringToNumber(val)
            handleChange(num === null ? null : patchEnergy({ lower: num }))
          }}
          onBlur={handleBlur}
        />

        {showRange ? (
          <>
            <span>-</span>

            <StyledEnergyInput
              value={numberToString(energy ? energy.upper : null)}
              placeholder={t("nutrition.component.values.energy.upper")}
              filter={/^\d*\.?\d*$/}
              onChange={(val) => handleChange(patchEnergy({ upper: stringToNumber(val) }))}
              onBlur={handleBlur}
            />
          </>
        ) : null}
      </Selectors>

      {error !== undefined && <Error>{error}</Error>}

      <RangeCheckbox
        $visible={energy !== null && energy.lower !== null}
        value={showRange}
        text={t("nutrition.component.values.energy.add_range")}
        onChange={(value) => {
          if (value === false) {
            handleChange(patchEnergy({ upper: null }))
          }
          setShowRange(value)
        }}
      />
    </Container>
  )
}

export default EnergySelection
